import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  TextField,
  Alert,
  CircularProgress
} from '@mui/material';
import {
  Cancel,
  Close,
  Warning
} from '@mui/icons-material';
import orderService from '../services/orderService';

const RejectOrderDialog = ({ open, onClose, order, onSuccess }) => {
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleReject = async () => {
    try {
      setLoading(true);
      setError('');

      if (!reason.trim()) {
        setError('Debes proporcionar un motivo de rechazo');
        setLoading(false);
        return;
      }

      const result = await orderService.reject(order.id, {
        rejectionReason: reason.trim()
      });

      if (result.success) {
        onSuccess(result.data);
        setReason('');
      } else {
        setError(result.message);
      }
    } catch (err) {
      console.error('Error al rechazar pedido:', err);
      setError('Error al rechazar el pedido');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Cancel color="error" />
          <Typography variant="h6">
            Rechazar Pedido #{order?.orderNumber}
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Alert severity="warning" icon={<Warning />} sx={{ mb: 3 }}>
          <Typography variant="body2" gutterBottom>
            <strong>Atención:</strong> Esta acción rechazará el pedido definitivamente.
          </Typography>
          <Typography variant="body2">
            El cliente podrá ver el motivo del rechazo en su historial de pedidos.
          </Typography>
        </Alert>

        <Typography variant="subtitle2" gutterBottom>
          Información del Pedido:
        </Typography>
        <Box sx={{ p: 2, bgcolor: 'background.default', borderRadius: 1, mb: 3 }}>
          <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
            <strong>Cliente:</strong> {order?.customer?.fullName || 'N/A'}
          </Typography>
          <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
            <strong>Email:</strong> {order?.customer?.email || order?.customer?.username || 'N/A'}
          </Typography>
          <Typography variant="body2">
            <strong>Productos:</strong> {order?.items?.length || 0} item(s)
          </Typography>
          <Typography variant="body2">
            <strong>Total:</strong> {new Intl.NumberFormat('es-AR', {
              style: 'currency',
              currency: 'ARS'
            }).format(order?.total || 0)}
          </Typography>
        </Box>

        <TextField
          fullWidth
          required
          multiline
          rows={4}
          label="Motivo del Rechazo"
          placeholder="Ej: Producto agotado, precio desactualizado, cliente no responde..."
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          disabled={loading}
          error={!reason.trim() && error !== ''}
          helperText="Este motivo será visible para el cliente"
        />
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button
          onClick={onClose}
          disabled={loading}
          startIcon={<Close />}
        >
          Cancelar
        </Button>
        <Button
          onClick={handleReject}
          variant="contained"
          color="error"
          disabled={loading || !reason.trim()}
          startIcon={loading ? <CircularProgress size={20} /> : <Cancel />}
        >
          {loading ? 'Rechazando...' : 'Confirmar Rechazo'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RejectOrderDialog;

